/* eslint-disable react/forbid-prop-types */
import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
// redux
import { useSelector, dispatch } from 'src/redux/store';
import { getStripe, selectStripeIsLiveOrPending } from 'src/redux/slices/practiceIntegration';
// api
import { sendPaymentLink, getChannelError, getEligibleWhatsappTemplates } from './paymentLinkApi';
import { getPaymentLinkChannelReadiness } from './paymentLinkReadiness';
import PaymentLinkChannelFields from './PaymentLinkChannelFields';
import PaymentLinkResultSummary from './PaymentLinkResultSummary';

// ─── Create & send / recover a payment link ─────────────────────────────────
// Recovery flow for a pending payment (paid_at null) that has no open
// checkout session (contract A, section "Send payment link"). Also reachable
// with replace_expired for an expired checkout — see checkoutTableRow.jsx.
export default function CreateSendPaymentLinkDialog({
    open,
    onClose,
    practiceId,
    leadId,
    treatmentId,
    payment,
    leadDetail,
    replaceExpired,
    onSuccess,
    appointmentId,
}) {
    const defaultEmail = leadDetail?.lead_contact?.email || leadDetail?.email || '';
    const defaultPhone = leadDetail?.lead_contact?.phone || leadDetail?.phone || leadDetail?.phone_number || '';

    const [emailChecked, setEmailChecked] = useState(Boolean(defaultEmail));
    const [whatsappChecked, setWhatsappChecked] = useState(Boolean(defaultPhone) && !replaceExpired);
    const [email, setEmail] = useState(defaultEmail);
    const [phone, setPhone] = useState(defaultPhone);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // success response
    const [errorText, setErrorText] = useState('');
    const [errorChannels, setErrorChannels] = useState(null);
    const [linkExpired, setLinkExpired] = useState(false);

    // ─── Channel readiness (mirrors backend _validate_prerequisites) ───────
    // Pre-flight state so the dialog can disable a channel + explain why
    // *before* a send attempt, instead of only after a 400 comes back.
    const practiceSettings = useSelector((state) => {
        const loaded = state.practice?.practice;
        // STALE-PRACTICE GUARD (mirrors useIsFollowUpV2Practice):
        // state.practice.practice lags after a practice switch — it can still hold
        // the PREVIOUS practice's settings until getPracticeItem(practiceId) resolves.
        // Without this, switching leads/practices could briefly show this practice's
        // template as configured/missing based on someone else's settings. Only trust
        // it when the loaded practice IS this dialog's practiceId; otherwise treat
        // template readiness as unknown (don't block) until it resolves.
        if (practiceId != null && loaded?.id != null && String(loaded.id) !== String(practiceId)) {
            return undefined;
        }
        return loaded?.settings;
    });
    const stripeConnected = useSelector(selectStripeIsLiveOrPending);
    const [whatsappEligibility, setWhatsappEligibility] = useState(null);

    useEffect(() => {
        if (!open || !practiceId) return;
        // silent: true — this is a background readiness probe, not a user action;
        // a failure here shouldn't pop an unrelated error toast (see getStripe).
        dispatch(getStripe(practiceId, { silent: true }));
    }, [open, practiceId]);

    useEffect(() => {
        if (!open || !practiceId) return undefined;
        let cancelled = false;
        setWhatsappEligibility(null);
        Promise.resolve(getEligibleWhatsappTemplates(practiceId))
            .then((res) => {
                if (cancelled) return;
                if (res?.status === 200 && res.data) {
                    setWhatsappEligibility({
                        connected: Boolean(res.data.connected),
                        results: Array.isArray(res.data.results) ? res.data.results : [],
                    });
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [open, practiceId]);

    const readiness = getPaymentLinkChannelReadiness({
        leadDetail,
        hasEmailOnFile: Boolean(email || defaultEmail),
        hasPhoneOnFile: Boolean(phone || defaultPhone),
        practiceSettings,
        stripeConnected,
        whatsappEligibility,
    });

    useEffect(() => {
        if (!open) return;
        setEmailChecked(Boolean(defaultEmail));
        setWhatsappChecked(Boolean(defaultPhone) && !replaceExpired);
        setEmail(defaultEmail);
        setPhone(defaultPhone);
        setSubmitting(false);
        setResult(null);
        setErrorText('');
        setErrorChannels(null);
        setLinkExpired(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, payment?.id]);

    // Force-uncheck a channel the moment it becomes structurally blocked
    // (template missing, WhatsApp disconnected, Stripe disconnected, ...) so
    // a stale "checked" state from before the readiness data loaded can't
    // sneak a blocked channel into the submit payload.
    useEffect(() => {
        if (readiness.email.blocking && emailChecked) setEmailChecked(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readiness.email.blocking]);

    useEffect(() => {
        if (readiness.whatsapp.blocking && whatsappChecked) setWhatsappChecked(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readiness.whatsapp.blocking]);

    const submit = useCallback(
        async (withReplaceExpired) => {
            const channels = [];
            if (emailChecked) channels.push('email');
            if (whatsappChecked) channels.push('whatsapp');
            if (channels.length === 0) {
                setErrorText('Select at least one channel.');
                return;
            }
            setSubmitting(true);
            setErrorText('');
            setErrorChannels(null);
            setLinkExpired(false);
            try {
                const payload = {
                    payment_type: payment.payment_type,
                    amount: payment.amount,
                    channels,
                    payment_id: payment.id,
                    replace_expired: Boolean(withReplaceExpired || replaceExpired),
                    // Contract: the payment-LINK command (this endpoint) takes
                    // `appointment_id` — NOT `appointment` (that field name is
                    // only for the plain payments create/update endpoint). A
                    // wrong id 400s with code `appointment_invalid`; errorText
                    // below (extractApiErrorMessage) already surfaces its
                    // detail text generically. `payment.appointment` (already
                    // set on the payment being recovered/replaced) wins over
                    // the caller-supplied appointmentId, which is only a
                    // fallback for a payment that predates the field.
                    ...((payment?.appointment ?? appointmentId) != null ? { appointment_id: payment?.appointment ?? appointmentId } : {}),
                };
                if (emailChecked && email) payload.email = email;
                if (whatsappChecked && phone) payload.phone = phone;

                // sendPaymentLink delegates to paymentLinkThunks.js and returns
                // { ok, data, error, status } — `error` is already the extracted
                // detail/message/field text, so no second extraction pass here.
                const res = await sendPaymentLink(practiceId, leadId, treatmentId, payload);
                if (res?.ok && res.status === 201 && res.data) {
                    setResult(res.data);
                    if (onSuccess) onSuccess();
                } else if (res?.status === 409) {
                    setLinkExpired(true);
                    setErrorText(res?.error || 'This payment link has expired.');
                } else {
                    setErrorText(res?.error || 'Something went wrong sending the payment link.');
                    setErrorChannels(res?.data?.channels || null);
                }
            } catch {
                setErrorText('Something went wrong sending the payment link.');
            } finally {
                setSubmitting(false);
            }
        },
        [emailChecked, whatsappChecked, email, phone, payment, practiceId, leadId, treatmentId, replaceExpired, onSuccess, appointmentId],
    );

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth data-testid="create-send-payment-link-dialog">
            <DialogTitle>{replaceExpired ? 'Replace expired payment link' : 'Create & send payment link'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {!result && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Choose how to deliver this payment link to the patient.
                            </Typography>

                            <PaymentLinkChannelFields
                                readiness={readiness}
                                emailChecked={emailChecked}
                                onEmailCheckedChange={setEmailChecked}
                                whatsappChecked={whatsappChecked}
                                onWhatsappCheckedChange={setWhatsappChecked}
                                email={email}
                                onEmailChange={setEmail}
                                phone={phone}
                                onPhoneChange={setPhone}
                                emailError={getChannelError({ channels: errorChannels }, 'email')}
                                whatsappError={getChannelError({ channels: errorChannels }, 'whatsapp')}
                            />

                            {errorText && (
                                <Alert severity={linkExpired ? 'warning' : 'error'}>{errorText}</Alert>
                            )}
                        </>
                    )}

                    {result && (
                        <Stack spacing={1.5}>
                            <Alert severity="success">Payment link processed.</Alert>
                            <PaymentLinkResultSummary result={result} />
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                {!result && (
                    <>
                        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                        {linkExpired || replaceExpired ? (
                            <Button
                                variant="contained"
                                onClick={() => submit(true)}
                                disabled={submitting || readiness.stripeConnected === false}
                                data-testid="replace-expired-link-confirm"
                            >
                                {submitting ? <CircularProgress size={18} /> : 'Replace expired link'}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => submit(false)}
                                disabled={submitting || readiness.stripeConnected === false}
                                data-testid="send-payment-link-confirm"
                            >
                                {submitting ? <CircularProgress size={18} /> : 'Send'}
                            </Button>
                        )}
                    </>
                )}
                {result && (
                    <Button variant="contained" onClick={onClose} data-testid="payment-link-result-close">
                        Done
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

CreateSendPaymentLinkDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.any,
    leadId: PropTypes.any,
    treatmentId: PropTypes.any,
    payment: PropTypes.object,
    leadDetail: PropTypes.object,
    replaceExpired: PropTypes.bool,
    onSuccess: PropTypes.func,
    appointmentId: PropTypes.any,
};

CreateSendPaymentLinkDialog.defaultProps = {
    practiceId: null,
    leadId: null,
    treatmentId: null,
    payment: null,
    leadDetail: null,
    replaceExpired: false,
    onSuccess: null,
    appointmentId: null,
};
